import "./form-person.scss";
import { useFormContext } from "react-hook-form";
import { PersonFormSchema } from "@factory/form/form.rule";
import InputControl from "@shared/ui/hook-form/input-control/input-control";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import CheckboxControl from "@shared/ui/hook-form/checkbox-control/checkbox-control";
import DropdownControl from "@shared/ui/hook-form/dropdown-control/dropdown-control";
import { useQyGetTenant } from "@factory/query/tenant.query";
import { LabelValue } from "@shared/models/label-value.interface";
import { useQyGetRole } from "@factory/query/role.query";
import { Divider } from "primereact/divider";
import ImageControl from "@shared/ui/hook-form/image-control/image-control";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { LocalRole } from "@core/model/local-role.enum";
import { useCallback } from "react";
import TextBlock from "@shared/ui/text-block/text-block";

type InputFormSchema = PersonFormSchema;
export function FormPerson() {
  const { control, watch } = useFormContext<PersonFormSchema>();
  const formRole = watch("role");
  const { data: tenantResponse } = useQyGetTenant("", 9999, 0);
  const tenantData = tenantResponse?.data || [];
  const tenantOptions = LabelValue.MapArray(
    tenantData,
    (item) => item.business_name,
    (item) => item.code
  );

  const { data: roleResponse } = useQyGetRole("", 9999, 0);
  const roleData = roleResponse?.data || [];
  const mappedRole = roleData.map(
    (item) =>
      ({
        label: item.name,
        value: item.code,
      } as LabelValue)
  );

  const { getUserRole } = useUserIdentityContext();
  const { name } = getUserRole();
  const roleName = name.toUpperCase();
  const selectedFormRole = mappedRole.find((item) => item.value === formRole);
  const availableRolesForManager = [LocalRole.Manager, LocalRole.Cashier];
  const filteredRole = useCallback(() => {
    switch (roleName) {
      case LocalRole.Admin:
        return mappedRole;

      default: {
        const availableRoles = mappedRole.filter((item) =>
          availableRolesForManager.includes(
            item.label.toUpperCase() as LocalRole
          )
        );
        return availableRoles;
      }
    }
  }, [roleName, mappedRole, availableRolesForManager]);

  const roleElement = useCallback(() => {
    if (roleName === LocalRole.Admin || roleName === LocalRole.Manager) {
      return (
        <DropdownControl<InputFormSchema>
          control={control}
          name="role"
          label="Role"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter role"
          options={filteredRole()}
        />
      );
    } else {
      return (
        <TextBlock
          className="mb-10 block"
          label="Role"
          value={selectedFormRole?.label}
        />
      );
    }
  }, [roleName, control, filteredRole, selectedFormRole?.label]);

  return (
    <div id="FormPerson" className="person">
      <ImageControl<InputFormSchema>
        control={control}
        name="photo"
        label="Person Photo"
        imagePreviewClass="w-32 h-32"
      />

      <Divider />
      <section className="block mt-10">
        {roleName.toUpperCase() === LocalRole.Admin ? (
          <DropdownControl<InputFormSchema>
            control={control}
            name="tenant"
            label="Tenant"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter tenant"
            options={tenantOptions}
          />
        ) : null}
        {roleElement()}
        <InputControl<InputFormSchema>
          control={control}
          name="firstName"
          label="First Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter firstName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="lastName"
          label="Last Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter lastName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="email"
          label="Email"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter email"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="username"
          label="Username"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter username"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="password"
          label="Password"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter password"
          type="password"
        />
        <CheckboxControl<InputFormSchema>
          control={control}
          name="isBlocked"
          label="Is Blocked"
          containerClassName="pb-2"
        />
      </section>

      {/* MORE FIELDS HIDDEN ATM */}
      <section className="hidden">
        <CheckboxControl<InputFormSchema>
          control={control}
          name="isVerified"
          label="Is Verified"
          containerClassName="pb-2"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="avatar"
          label="Avatar"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter avatar"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="gender"
          label="Gender"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter gender"
        />
        <InputDateControl<InputFormSchema>
          control={control}
          name="birthday"
          label="Birthday"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="mobileNumber"
          label="Mobile Number"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter mobileNumber"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="fatherName"
          label="Father Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter fatherName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="motherName"
          label="Mother Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter motherName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_1Number"
          label="Emergency Contact 1 Number"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_1Number"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_1Name"
          label="Emergency_contact_1_name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_1Name"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_1Relationship"
          label="Emergency_contact_1_relationship"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_1Relationship"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_2Number"
          label="Emergency_contact_2_number"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_2Number"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_2Name"
          label="Emergency_contact_2_name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_2Name"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_2Relationship"
          label="Emergency_contact_2_relationship"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_2Relationship"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="sss"
          label="Sss"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter sss"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="philhealth"
          label="Philhealth"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter philhealth"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="pagibig"
          label="Pagibig"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter pagibig"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="tin"
          label="Tin"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter tin"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="medicalCondition"
          label="Medical_condition"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter medicalCondition"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="bloodType"
          label="Blood_type"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter bloodType"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="elementary"
          label="Elementary"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter elementary"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="highSchool"
          label="High_school"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter highSchool"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="college"
          label="College"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter college"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="postCollege"
          label="Post_college"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter postCollege"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="undergraduate"
          label="Undergraduate"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter undergraduate"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="curriculumVitae"
          label="Curriculum_vitae"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter curriculumVitae"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="supportingDoc"
          label="Supporting_doc"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter supportingDoc"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="intentLetter"
          label="Intent_letter"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter intentLetter"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="applicationLetter"
          label="Application_letter"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter applicationLetter"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="address"
          label="Address"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter address"
        />
      </section>
    </div>
  );
}

export default FormPerson;
